import * as Collapsible from '@radix-ui/react-collapsible'
import { Text, Button, Dialog, Flex, Table, Callout } from '@radix-ui/themes'
import bytes from 'bytes'
import { useState } from 'react'
import { Accept, useDropzone } from 'react-dropzone'
import { LuArrowDown, LuChevronDown, LuChevronRight, LuFileSpreadsheet } from 'react-icons/lu'
import Papa from 'papaparse'
import { DateTime } from 'luxon'
import { ManualDatapoint } from '../../schemas'
import { OrgAssetMeta, uploadOrgAsset } from '~/features/orgnisations/service'
import { useCurrentOrg } from '~/features/orgnisations/hooks/useCurrentOrg'
import { useTranslation } from 'react-i18next'

const MAX_UPLOAD_SIZE = bytes('10MB') as number
const ACCEPTED_FILES: Accept = {
  'text/csv': ['.csv'],
  'application/csv': ['.csv'],
  'text/comma-separated-values': ['.csv']
}

function readRow<TParse extends (v: string) => any>(
  row: Record<string, string>,
  keys: string[],
  parser: TParse
): ReturnType<TParse> {
  for (const key of keys) {
    if (row[key] !== undefined && row[key] !== null && row[key] !== '') {
      return parser(row[key])
    }
  }
  throw new Error(`No valid value found for keys: ${keys.join(', ')}`)
}

function parseDate(dStr: string): DateTime {
  const d = DateTime.fromISO(dStr.trim())
  if (!d.isValid) {
    throw new Error(`Invalid date: ${dStr}`)
  }
  return d
}

function parseValue(vStr: string): number {
  const v = parseFloat(vStr.trim())
  if (isNaN(v)) {
    throw new Error(`Invalid value: ${vStr}`)
  }
  return v
}

// Table component for displaying datapoints
function DataTable({ data }: { data: { date: DateTime | string; consumption: number }[] }) {
  const { t } = useTranslation('sites')
  const maxRows = 5
  const showMore = data.length > maxRows
  const visibleRows = data.slice(0, maxRows)
  return (
    <>
      <Table.Root>
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeaderCell>{t('stations.datasourceSection.table.date')}</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>{t('stations.datasourceSection.table.value')}</Table.ColumnHeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {visibleRows.map((row, i) => (
            <Table.Row key={i}>
              <Table.Cell>{typeof row.date === 'string' ? row.date : row.date.toISODate()}</Table.Cell>
              <Table.Cell>{row.consumption}</Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>
      {showMore && (
        <Text size="1" color="gray" mt="1" align="center">
          {t('stations.datasourceSection.table.additionalPoints', { count: data.length - maxRows })}
        </Text>
      )}
    </>
  )
}

const Example = () => {
  const { t } = useTranslation('sites')
  const [open, setOpen] = useState(false)
  const exampleData = [
    { date: '2024-01-01', consumption: 123 },
    { date: '2024-01-02', consumption: 450 },
    { date: '2024-01-02', consumption: 212 }
  ]
  return (
    <Collapsible.Root open={open} onOpenChange={setOpen}>
      <Collapsible.Trigger asChild>
        <Button variant="ghost" color="blue" size={'2'}>
          {open ? (
            <>
              {t('stations.datasourceSection.example.hide')} <LuChevronDown size={18} />
            </>
          ) : (
            <>
              {t('stations.datasourceSection.example.show')} <LuChevronRight size={18} />
            </>
          )}
        </Button>
      </Collapsible.Trigger>
      <Collapsible.Content>
        <DataTable data={exampleData} />
      </Collapsible.Content>
    </Collapsible.Root>
  )
}

interface CSVImportDialogProps {
  onConfirm: (datapoints: ManualDatapoint[], info: OrgAssetMeta) => void
  stationID: string
}

const CSVImportDialog = ({ onConfirm, stationID }: CSVImportDialogProps) => {
  const { t } = useTranslation('sites')
  const [error, setError] = useState<string>('')
  const [datapoints, setDatapoints] = useState<ManualDatapoint[]>([])
  const { id: orgID } = useCurrentOrg()
  const [file, setFile] = useState<File | null>(null)

  const { getRootProps, getInputProps, isDragActive, inputRef } = useDropzone({
    multiple: false,
    accept: ACCEPTED_FILES,
    maxSize: MAX_UPLOAD_SIZE,
    onDropRejected: (rejectedFiles) => {
      setError(
        t('stations.datasourceSection.errors.fileNotAccepted', {
          names: rejectedFiles.map((f) => f.file.name).join(', ')
        })
      )
    },
    onDrop: async ([file]) => {
      if (!file) {
        return
      }
      setFile(file)
      setError('')
      setDatapoints([])
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results: Papa.ParseResult<Record<string, string>>) => {
          const rows = results.data
          const metaFields = results.meta.fields || []

          const dateHeaders = ['Date', 'date']
          const valueHeaders = ['Valeur', 'valeur', 'Value', 'value']

          const hasDate = metaFields.some((h) => dateHeaders.includes(h))
          const hasValue = metaFields.some((h) => valueHeaders.includes(h))
          if (!hasDate || !hasValue) {
            setError(t('stations.datasourceSection.errors.missingColumns'))
            setDatapoints([])
            return
          }

          try {
            const points = rows.map((row) => {
              const date = readRow(row, dateHeaders, parseDate)
              const consumption = readRow(row, valueHeaders, parseValue)
              return { date, consumption }
            })
            setDatapoints(points)
          } catch (e) {
            setError(t('stations.datasourceSection.errors.invalidRow'))
            setDatapoints([])
          }
        },
        error: () => {
          setError(t('stations.datasourceSection.errors.readError'))
        }
      })
    }
  })

  const clearState = () => {
    setError('')
    if (inputRef.current) {
      inputRef.current.value = '' // Clear the file input
    }
    setDatapoints([])
  }

  const handleConfirm = async () => {
    if (!file || !datapoints || datapoints.length === 0) {
      return
    }
    const assetMeta = await uploadOrgAsset(orgID, file, `/csv-imports/${stationID}/`)
    onConfirm(datapoints, assetMeta)
    clearState()
  }

  return (
    <Dialog.Root>
      <Dialog.Trigger>
        <Button variant="soft">
          <LuFileSpreadsheet size={18} />
          {t('stations.datasourceSection.importCSVButton')}
        </Button>
      </Dialog.Trigger>
      <Dialog.Content>
        <Dialog.Title>{t('stations.datasourceSection.importDialogTitle')}</Dialog.Title>
        <Flex direction={'column'} gap="4" mb="4">
          <Dialog.Description>
            <Text size="2" mb="2" color="gray">
              {t('stations.datasourceSection.importDialogDescription', {
                date: t('stations.datasourceSection.table.date'),
                value: t('stations.datasourceSection.table.value')
              })}
            </Text>
          </Dialog.Description>
          {datapoints.length > 0 ? (
            <>
              <Callout.Root>
                <Callout.Text>
                  <Text size="2">
                    {t('stations.datasourceSection.importedPoints', { count: datapoints.length })}
                  </Text>
                </Callout.Text>
              </Callout.Root>
              <DataTable data={datapoints} />
            </>
          ) : (
            <>
              <Example />
              <Flex
                p="4"
                align={'center'}
                justify={'center'}
                direction={'column'}
                className="border-2 border-dashed rounded-md w-full border-gray-5 h-36"
                {...getRootProps()}
              >
                {isDragActive ? (
                  <>
                    <LuArrowDown size={25} className="text-gray-500 mb-3" />
                    <Text weight="medium">{t('stations.datasourceSection.dropzone.active')}</Text>
                    <Text size="1" color="gray"></Text>
                  </>
                ) : (
                  <>
                    <LuFileSpreadsheet size={25} className="text-gray-500 mb-3" />
                    <Text weight="medium">{t('stations.datasourceSection.dropzone.title')}</Text>
                    <Text size="1" color="gray">
                      {t('stations.datasourceSection.dropzone.subtitle')}
                    </Text>
                  </>
                )}
                <input {...getInputProps()} />
              </Flex>
              {error ? (
                <Text size="1" color="red" align="center" mt={'-2'}>
                  {error}
                </Text>
              ) : (
                <Text size="1" color="gray" align="center" mt={'-2'}>
                  {t('stations.datasourceSection.supportedFormat')}
                </Text>
              )}
            </>
          )}
        </Flex>
        <Flex justify="end" mt={'5'} gap="2">
          <Dialog.Close>
            <Button variant="soft" color="gray" onClick={clearState}>
              {t('stations.datasourceSection.cancel')}
            </Button>
          </Dialog.Close>
          <Dialog.Close>
            <Button disabled={!!error || datapoints.length === 0} onClick={handleConfirm}>
              {t('stations.datasourceSection.confirm')}
            </Button>
          </Dialog.Close>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  )
}

export default CSVImportDialog
